// Types for Fundraise Pipeline feature

export interface PipelineStage {
  id: string;
  name: string;
  color: string;
}

export interface Fund {
  id: string;
  pipeline_id: string;
  user_id: string;
  name: string;
  min_amount: number | null;
  max_amount: number | null;
  stage: string;
  position: number;
  logo_initials: string | null;
  logo_color: string;
  website_url: string | null;
  contact_name: string | null;
  contact_email: string | null;
  notes: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface PipelineCollaborator {
  id: string;
  pipeline_id: string;
  user_id: string;
  share_link_id: string | null;
  joined_at: string;
  email?: string | null;
  full_name?: string | null;
}

export interface Pipeline {
  id: string;
  user_id: string;
  name: string;
  target_amount: number;
  stages: PipelineStage[];
  is_expanded: boolean;
  created_at: string;
  updated_at: string;
  // Set by the API when this pipeline is shared with the requesting user
  // as a collaborator (not the owner). Surfaced for the "Shared by X" badge
  // and to hide owner-only controls (Delete, Share, etc.).
  is_collaborator?: boolean;
  owner_email?: string | null;
  owner_name?: string | null;
  funds?: Fund[];
}

export interface CreatePipelineInput {
  name: string;
  target_amount: number;
  stages: PipelineStage[];
}

export interface UpdatePipelineInput {
  name?: string;
  target_amount?: number;
  stages?: PipelineStage[];
  is_expanded?: boolean;
}

export interface CreateFundInput {
  pipeline_id: string;
  name: string;
  min_amount?: number | null;
  max_amount?: number | null;
  stage: string;
  logo_initials?: string;
  logo_color?: string;
  website_url?: string;
  contact_name?: string;
  contact_email?: string;
  notes?: string;
}

export interface UpdateFundInput {
  name?: string;
  min_amount?: number | null;
  max_amount?: number | null;
  stage?: string;
  position?: number;
  logo_initials?: string;
  logo_color?: string;
  website_url?: string;
  contact_name?: string;
  contact_email?: string;
  notes?: string;
}

export interface FundraiseStats {
  total_pipelines: number;
  total_funds: number;
  combined_target: number;
  active_discussions: number;
}

// Default stages configuration
export const DEFAULT_STAGES: PipelineStage[] = [
  { id: 'research', name: 'Research', color: 'purple' },
  { id: 'contacted', name: 'Contacted', color: 'yellow' },
  { id: 'meeting', name: 'Meeting', color: 'green' },
  { id: 'light_diligence', name: 'Light Diligence', color: 'blue' },
  { id: 'partner_meeting', name: 'Partner Meeting', color: 'pink' },
  { id: 'term_sheet', name: 'Term Sheet', color: 'orange' },
  { id: 'closed', name: 'Closed', color: 'emerald' },
  { id: 'keep_in_touch', name: 'Keep in Touch', color: 'gray' },
  { id: 'passed', name: 'Passed', color: 'red' },
];

// Stages that count as "active discussions" (industry best practice)
export const ACTIVE_DISCUSSION_STAGES = [
  'contacted',
  'meeting',
  'light_diligence',
  'partner_meeting',
  'term_sheet',
];

// Stage color mappings for UI
export const STAGE_COLORS: Record<string, string> = {
  purple: '#8b5cf6',
  yellow: '#eab308',
  green: '#22c55e',
  blue: '#3b82f6',
  pink: '#ec4899',
  orange: '#f97316',
  emerald: '#10b981',
  gray: '#6b7280',
  red: '#ef4444',
};

// Pipeline sharing

export type PipelineSharePermission = 'view' | 'collaborate';

export interface PipelineShareLink {
  id: string;
  pipeline_id: string;
  user_id: string;
  share_token: string;
  url_slug: string | null;
  permission_level: PipelineSharePermission;
  label: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CreatePipelineShareLinkInput {
  label?: string;
  expires_at?: string;
  permission_level?: PipelineSharePermission;
}

// Slugify a name for use as the business segment in pipeline share URLs.
const slugifyPipelineName = (name: string): string => {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'untitled';
};

// Build the public share URL. Prefer url_slug + pipeline name; fall back to
// the raw token-style /pipeline/:token route for legacy or null-slug links.
export const getPipelineShareUrl = (
  urlSlugOrToken: string,
  pipelineName?: string
): string => {
  const baseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://sendupdates.ai';
  if (pipelineName && urlSlugOrToken.startsWith('pipeline-')) {
    return `${baseUrl}/pipeline/${slugifyPipelineName(pipelineName)}/${urlSlugOrToken}`;
  }
  return `${baseUrl}/pipeline/${urlSlugOrToken}`;
};

// Logo color options for funds
export const FUND_LOGO_COLORS = [
  { id: 'purple', color: '#8b5cf6', bg: '#8b5cf6' },
  { id: 'blue', color: '#3b82f6', bg: '#3b82f6' },
  { id: 'green', color: '#22c55e', bg: '#22c55e' },
  { id: 'yellow', color: '#eab308', bg: '#eab308' },
  { id: 'pink', color: '#ec4899', bg: '#ec4899' },
  { id: 'orange', color: '#f97316', bg: '#f97316' },
  { id: 'red', color: '#ef4444', bg: '#ef4444' },
  { id: 'gray', color: '#6b7280', bg: '#6b7280' },
];
